import { isProd } from '@/constant/env';
import { Entry } from '../types/entry';
import { APIPath } from './constants';
import { getEntries } from './getEntries';

export const CONTENT_VALIDATED_API_PATHS = [
  '/pages',
  '/blogs',
  '/blog-topics',
] as const satisfies readonly APIPath[];

export function shouldValidateEntryContent(apiPath: APIPath): boolean {
  return (CONTENT_VALIDATED_API_PATHS as readonly APIPath[]).includes(apiPath);
}

/**
 * Empty CMS responses are only treated as errors in production;
 * in other environments incomplete entries are expected (AWD-6168).
 */
export const shouldCheckForEmptyBlocks = (): boolean => isProd;

export type EntryContent = Partial<
  Pick<Entry, 'slug' | 'blocks' | 'header_reference' | 'footer_reference'>
>;

type EntityContentCMSPath = '/headers' | '/footers';

/**
 * Landing pages (lp/*) intentionally have no footer, so the
 * footer_reference check is skipped for them.
 */
function isFooterOptional(entry: EntryContent): boolean {
  return entry.slug?.startsWith('lp/') ?? false;
}
export function getMissingContentIssues(
  entry: EntryContent | null | undefined
): string[] {
  if (!entry) {
    return ['entry has no attributes'];
  }

  const issues: string[] = [];

  if (!Array.isArray(entry.blocks)) {
    issues.push("'blocks' relation is missing");
  } else if (entry.blocks.length === 0) {
    issues.push("'blocks' is empty");
  }

  if (!entry.header_reference?.data?.documentId) {
    issues.push("'header_reference' relation is missing");
  }

  if (!entry.footer_reference?.data?.documentId && !isFooterOptional(entry)) {
    issues.push("'footer_reference' relation is missing");
  }

  return issues;
}

async function getReferencedEntityErrors(
  apiPath: EntityContentCMSPath,
  documentId: string,
  relation: 'header_reference' | 'footer_reference'
): Promise<string[]> {
  const response = await getEntries({
    apiPath,
    query: {
      pLevel: 'deep',
      filters: {
        documentId: { $eq: documentId },
      },
      pagination: {
        page: 1,
        pageSize: 1,
      },
    },
    fetchOptions: { cache: 'no-store' },
  });

  const entity = response?.data?.[0];

  if (!entity) {
    return [`'${relation}' (${documentId}) CMS entry not found`];
  }

  const attributes = entity.attributes as any;

  let hasPayload = false;

  if (apiPath === '/headers') {
    hasPayload = !!(attributes?.header ?? attributes?.logo);
  } else {
    hasPayload = !!(attributes?.footer ?? attributes?.bentoFooter);
  }

  if (!hasPayload) {
    return [`'${relation}' (${documentId}) has no CMS payload`];
  }

  return [];
}

/** Validates referenced header/footer entities with a minimal CMS fetch. */
export async function getMissingReferencedEntityIssues(
  entry: EntryContent
): Promise<string[]> {
  const issues: string[] = [];
  const headerDocumentId = entry.header_reference?.data?.documentId;

  if (headerDocumentId) {
    issues.push(
      ...(await getReferencedEntityErrors(
        '/headers',
        headerDocumentId,
        'header_reference'
      ))
    );
  }

  const footerDocumentId = entry.footer_reference?.data?.documentId;

  if (footerDocumentId && !isFooterOptional(entry)) {
    issues.push(
      ...(await getReferencedEntityErrors(
        '/footers',
        footerDocumentId,
        'footer_reference'
      ))
    );
  }

  return issues;
}

export async function validateEntryContent(
  entry: EntryContent
): Promise<string[]> {
  const issues = getMissingContentIssues(entry);

  if (issues.length > 0) {
    return issues;
  }

  return getMissingReferencedEntityIssues(entry);
}

export type MissingContentError = Error & {
  name: 'MissingContentError';
  apiPath?: APIPath;
  issues: string[];
};

export function createMissingContentError({
  apiPath,
  identifier,
  issues,
}: {
  apiPath?: APIPath;
  identifier?: string;
  issues: string[];
}): MissingContentError {
  const error = new Error(
    `Entry '${identifier ?? 'unknown'}'${
      apiPath ? ` (${apiPath})` : ''
    } is missing required content: ${issues.join(
      '; '
    )}. Empty CMS responses are treated as errors to prevent serving blank pages (AWD-6168).`
  ) as MissingContentError;

  error.name = 'MissingContentError';
  error.apiPath = apiPath;
  error.issues = issues;

  return error;
}

export function isMissingContentError(
  error: unknown
): error is MissingContentError {
  return error instanceof Error && error.name === 'MissingContentError';
}
